import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { forwardRef } from "react";
import './index.css';const getSpinnerSize = (size) => {
  switch (size) {
    case "xxsmall":
      return 16;
    case "xsmall":
      return 20;
    case "small":
      return 24;
    case "medium":
      return 48;
    case "large":
    default:
      return 96;
  }
};
const getStrokeSize = (size) => {
  switch (size) {
    case "xxsmall":
    case "xsmall":
      return getSpinnerSize(size) / 5;
    case "small":
    case "medium":
      return getSpinnerSize(size) / 6;
    case "large":
    default:
      return getSpinnerSize(size) / 10;
  }
};
const LoadingIndicatorSpinner = forwardRef(
  ({
    size = "large",
    loadingText,
    colorVariant = "onWhite",
    className,
    ...props
  }, ref) => {
    const spinnerSize = getSpinnerSize(size);
    const stroke = getStrokeSize(size);
    const cx = spinnerSize / 2;
    const cy = spinnerSize / 2;
    const innerRadius = spinnerSize / 2 - stroke / 2;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ...props,
        className: `spinnerContainer ${size} ${colorVariant}${className ? ` ${className}` : ""}`,
        "data-stb-spinner": "true",
        "data-component-info": "pretzel-spinner",
        role: "progressbar",
        "aria-busy": true,
        "aria-label": loadingText ?? "Loading...",
        ref,
        children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: `spinner ${colorVariant}`,
              version: "1.1",
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: `0 0 ${spinnerSize} ${spinnerSize}`,
              height: spinnerSize,
              width: spinnerSize,
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "spinnerTrack",
                    cx,
                    cy: cx,
                    r: innerRadius,
                    fill: "none",
                    strokeWidth: stroke
                  }
                ),
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "spinnerInMotion",
                    cx,
                    cy,
                    r: innerRadius,
                    fill: "none",
                    strokeWidth: stroke
                  }
                )
              ]
            }
          ),
          loadingText && size === "large" && /* @__PURE__ */ jsx("p", { className: "font-body", children: loadingText }),
          loadingText && size === "medium" && /* @__PURE__ */ jsx("p", { className: "font-small", children: loadingText })
        ]
      }
    );
  }
);
const buttonFontClassFrom = (size) => {
  switch (size) {
    case "large":
      return "font-button";
    case "small":
      return "font-button-small";
    case "medium":
    default:
      return "font-button-mobile";
  }
};
const toSpinnerSize = (size) => {
  switch (size) {
    case "small":
      return "xxsmall";
    case "medium":
      return "xsmall";
    case "large":
    default:
      return "small";
  }
};
const fixedForwardRef = forwardRef;
function ButtonWithForwardRef(props, ref) {
  const {
    disabled,
    variant = "primary",
    loading = false,
    iconLeft = "",
    iconRight = "",
    showDefaultIcon = true,
    onClick,
    children,
    size = "large",
    type = "button",
    className,
    id,
    as: Element = "button",
    ...rest
  } = props;
  const shouldHaveDefaultIcon = showDefaultIcon && variant === "tertiary" && !iconRight && !iconLeft;
  const shouldBeUnderlined = variant === "tertiary" && !iconRight && !iconLeft && !shouldHaveDefaultIcon;
  function handleClick() {
    if (disabled || loading) {
      return (e) => e.preventDefault();
    }
    return onClick;
  }
  function renderLoading() {
    return loading && /* @__PURE__ */ jsx(
      LoadingIndicatorSpinner,
      {
        size: toSpinnerSize(size),
        colorVariant: "onRed"
      }
    );
  }
  function renderContent() {
    return !loading && /* @__PURE__ */ jsxs(Fragment, { children: [
      iconLeft && /* @__PURE__ */ jsx("i", { className: "systemIcon iconLeft", translate: "no", "aria-hidden": true, children: iconLeft }),
      children && /* @__PURE__ */ jsx("span", { children }),
      shouldHaveDefaultIcon && /* @__PURE__ */ jsx("i", { className: "systemIcon iconRight", translate: "no", "aria-hidden": true, children: "arrow_right" }),
      iconRight && /* @__PURE__ */ jsx("i", { className: "systemIcon iconRight", translate: "no", "aria-hidden": true, children: iconRight })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    Element,
    {
      ...rest,
      id,
      ref,
      "data-stb-button": "true",
      "data-component-info": "pretzel-button",
      "data-loading": loading ? true : null,
      "data-variant": variant,
      "data-size": size,
      "aria-disabled": disabled,
      role: Element === "a" ? "link" : null,
      onClick: handleClick(),
      type: Element === "button" ? type : void 0,
      disabled,
      className: `button font-button-large ${buttonFontClassFrom(size)}${shouldBeUnderlined ? " underlined" : ""}${className ? ` ${className}` : ""}`,
      tabIndex: "0",
      href: Element === "a" ? rest.href : void 0,
      children: [
        renderLoading(),
        renderContent()
      ]
    }
  );
}
const Button = fixedForwardRef(ButtonWithForwardRef);
function ButtonBrandWithForwardedRef(props, ref) {
  const {
    disabled,
    loading = false,
    iconLeft = "",
    iconRight = "",
    onClick,
    children,
    as = "button",
    type = "button",
    className,
    as: Element = "button",
    ...rest
  } = props;
  function handleClick() {
    if (disabled || loading) {
      return (e) => e.preventDefault();
    }
    return onClick;
  }
  function renderLoading() {
    return loading && /* @__PURE__ */ jsx(LoadingIndicatorSpinner, { size: "small", colorVariant: "onRed" });
  }
  function renderContent() {
    return !loading && /* @__PURE__ */ jsxs(Fragment, { children: [
      iconLeft && /* @__PURE__ */ jsx("i", { className: "systemIcon iconLeft", translate: "no", "aria-hidden": true, children: iconLeft }),
      /* @__PURE__ */ jsx("span", { children }),
      iconRight && /* @__PURE__ */ jsx("i", { className: "systemIcon iconRight", translate: "no", "aria-hidden": true, children: iconRight })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    Element,
    {
      ...rest,
      ref,
      "data-stb-button-brand": "true",
      "data-component-info": "pretzel-button-brand",
      "data-loading": loading ? true : null,
      "aria-disabled": disabled,
      role: as === "a" ? "link" : null,
      onClick: handleClick(),
      type: as === "button" ? type : void 0,
      disabled,
      className: `buttonBrand font-button-large${className ? ` ${className}` : ""}`,
      tabIndex: "0",
      href: Element === "a" ? rest.href : void 0,
      children: [
        renderLoading(),
        renderContent()
      ]
    }
  );
}
const ButtonBrand = fixedForwardRef(ButtonBrandWithForwardedRef);
function ButtonCircleWithForwardedRef(props, ref) {
  const {
    disabled,
    variant = "primary",
    loading = false,
    iconName,
    onClick,
    as = "button",
    type = "button",
    className,
    "aria-label": ariaLabel,
    as: Element = "button",
    size = "large",
    ...rest
  } = props;
  function handleClick() {
    if (disabled || loading) {
      return (e) => e.preventDefault();
    }
    return onClick;
  }
  function renderLoading() {
    return loading && /* @__PURE__ */ jsx(
      LoadingIndicatorSpinner,
      {
        size: toSpinnerSize(size),
        colorVariant: "onRed"
      }
    );
  }
  function renderContent() {
    return !loading && iconName && /* @__PURE__ */ jsx("i", { className: "systemIcon", translate: "no", "aria-hidden": true, children: iconName });
  }
  return /* @__PURE__ */ jsxs(
    Element,
    {
      ...rest,
      ref,
      "data-stb-button-circle": "true",
      "data-component-info": "pretzel-button-circle",
      "data-loading": loading ? true : null,
      "data-variant": variant,
      "data-size": size,
      "aria-disabled": disabled,
      "aria-label": ariaLabel,
      role: as === "a" ? "link" : null,
      onClick: handleClick(),
      type: as === "button" ? type : void 0,
      disabled,
      className: `buttonCircle font-button-large${className ? ` ${className}` : ""}`,
      tabIndex: "0",
      href: Element === "a" ? rest.href : void 0,
      children: [
        renderLoading(),
        renderContent()
      ]
    }
  );
}
const ButtonCircle = fixedForwardRef(ButtonCircleWithForwardedRef);
function ButtonCircleBrandWithForwardedRef(props, ref) {
  const {
    disabled,
    loading = false,
    iconName,
    onClick,
    as = "button",
    type = "button",
    className,
    "aria-label": ariaLabel,
    as: Element = "button",
    ...rest
  } = props;
  function handleClick() {
    if (disabled || loading) {
      return (e) => e.preventDefault();
    }
    return onClick;
  }
  function renderLoading() {
    return loading && /* @__PURE__ */ jsx(LoadingIndicatorSpinner, { size: "small", colorVariant: "onRed" });
  }
  function renderContent() {
    return !loading && iconName && /* @__PURE__ */ jsx("i", { className: "systemIcon", translate: "no", "aria-hidden": true, children: iconName });
  }
  return /* @__PURE__ */ jsxs(
    Element,
    {
      ...rest,
      ref,
      "data-stb-button-circle-brand": "true",
      "data-component-info": "pretzel-button-circle-brand",
      "data-loading": loading ? true : null,
      "aria-disabled": disabled,
      "aria-label": ariaLabel,
      role: as === "a" ? "link" : null,
      onClick: handleClick(),
      type: as === "button" ? type : void 0,
      disabled,
      className: `buttonCircleBrand font-button-large${className ? ` ${className}` : ""}`,
      tabIndex: "0",
      href: Element === "a" ? rest.href : void 0,
      children: [
        renderLoading(),
        renderContent()
      ]
    }
  );
}
const ButtonCircleBrand = fixedForwardRef(
  ButtonCircleBrandWithForwardedRef
);
function ButtonIconWithForwardedRef(props, ref) {
  const {
    iconName,
    disabled,
    onClick,
    size = "medium",
    as = "button",
    type = "button",
    className,
    "aria-label": ariaLabel,
    as: Element = "button",
    ...rest
  } = props;
  function handleClick() {
    if (disabled) {
      return (e) => e.preventDefault();
    }
    return onClick;
  }
  return /* @__PURE__ */ jsx(
    Element,
    {
      ...rest,
      ref,
      "data-stb-button-icon": "true",
      "data-component-info": "pretzel-button-icon",
      "aria-disabled": disabled,
      "aria-label": ariaLabel,
      role: as === "a" ? "link" : null,
      onClick: handleClick(),
      "data-size": size,
      type: as === "button" ? type : void 0,
      disabled,
      className: `buttonIcon ${className ? ` ${className}` : ""}`,
      tabIndex: "0",
      href: Element === "a" ? rest.href : void 0,
      children: /* @__PURE__ */ jsx("i", { "aria-hidden": true, translate: "no", children: iconName })
    }
  );
}
const ButtonIcon = fixedForwardRef(ButtonIconWithForwardedRef);
const index = {
  Button,
  ButtonBrand,
  ButtonCircle,
  ButtonCircleBrand,
  ButtonIcon
};
export {
  Button,
  ButtonBrand,
  ButtonCircle,
  ButtonCircleBrand,
  ButtonIcon,
  index as default
};
